import { Component, OnInit, inject, output, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { faFileUpload } from '@fortawesome/pro-solid-svg-icons';
import { cloneDeep } from 'lodash-es';
import { filter, switchMap, take } from 'rxjs/operators';
import { assert } from 'src/app/core/assert/assert';
import { ProjectModel } from '../../../../core/models/project/project.model';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { ProjectTransformationService } from '../../../../core/services/project-transformation.service';
import { ProjectsHttpService } from '../../../../core/state/projects/projects-http.service';
import { TodoPlannerHttpService } from '../../../../core/state/todo-planner/todo-planner-http.service';
import { ProjectStatusFilter } from '../../../../pages/project/shared/project-detail-types';
import { DropdownOption } from '../../../../shared/data-types/dropdown-option';
import { recordToGroupedDropdownOptions } from '../../../../shared/functions/dropdown-functions';
import { TodoPlannerService } from '../../services/todo-planner.service';

@Component({
  selector: 'app-todo-assign-to-project',
  standalone: false,
  templateUrl: './todo-assign-to-project.component.html',
})
export class TodoAssignToProjectComponent implements OnInit {
  // Signals
  readonly projectsDropdownItems = signal<DropdownOption[]>([]);
  readonly assignProjectForm = signal<FormGroup | null>(null);
  readonly closePopup = output<TodoEntryModel>();

  // Constants
  public readonly faFileUpload = faFileUpload;
  public readonly formControlNames: Record<string, string> = {
    Project: 'project',
    UseColor: 'useColor',
  };

  private projects: ProjectModel[] = [];
  private todoEntryModel: TodoEntryModel = null;

  // DI
  private readonly projectsHttpService = inject(ProjectsHttpService);
  private readonly projectTransformationService = inject(ProjectTransformationService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly todoPlannerHttpService = inject(TodoPlannerHttpService);
  private readonly todoPlannerService = inject(TodoPlannerService);

  ngOnInit(): void {
    this.loadProjects();
    this.buildForm();

    this.todoPlannerService.sendTodoEntryModel$
      .pipe(
        filter((entry) => !!entry),
        switchMap((entry: TodoEntryModel) =>
          this.todoPlannerHttpService.getSingleTodoPlannerEntry(entry.id),
        ),
        take(1),
      )
      .subscribe((todoEntryModel) => {
        this.todoEntryModel = todoEntryModel;
      });
  }

  public assignToProject(): void {
    const selectedProject = this.assignProjectForm().controls[this.formControlNames.Project]
      .value as DropdownOption;
    const projectModel = this.projects.find((project) => project.id === selectedProject.value);

    assert(!!this.todoEntryModel && !!projectModel);

    const clone = cloneDeep(this.todoEntryModel);
    clone.projectId = projectModel.id;

    const useColor = !!this.assignProjectForm().controls[this.formControlNames.UseColor].value;
    if (useColor) {
      clone.color = projectModel.color;
    }

    this.closePopup.emit(clone);
  }

  private loadProjects(): void {
    this.projectsHttpService
      .getProjects(null, ProjectStatusFilter.Active)
      .pipe(
        take(1),
        switchMap((projects) => {
          this.projects = projects;

          return recordToGroupedDropdownOptions(
            this.projectTransformationService.groupProjectsByClients(projects),
          );
        }),
      )
      .subscribe((projects) => {
        this.projectsDropdownItems.set(projects);

        this.assignProjectForm().controls[this.formControlNames.Project].setValue(
          projects.length > 0 ? projects[0] : null,
        );
      });
  }

  private buildForm(): void {
    const projectForm = this.formBuilder.group({
      [this.formControlNames.Project]: [null],
      [this.formControlNames.UseColor]: [true],
    });
    this.assignProjectForm.set(projectForm);
  }
}
